import React, { forwardRef } from 'react';

/**
 * Button Component
 * A reusable button with multiple variants, sizes, and loading state
 */
const Button = forwardRef(({
  children,
  variant = 'primary',
  size = 'md',
  loading = false,
  disabled = false,
  type = 'button',
  className = '',
  leftIcon,
  rightIcon,
  ...props
}, ref) => {
  const baseStyles = `
    inline-flex items-center justify-center
    font-medium rounded-lg
    focus:outline-none focus:ring-2 focus:ring-offset-2
    transition-all duration-200
    disabled:opacity-50 disabled:cursor-not-allowed
  `;

  const variants = {
    primary: `
      bg-blue-600 hover:bg-blue-700 active:bg-blue-800
      text-white
      focus:ring-blue-500
      dark:bg-blue-500 dark:hover:bg-blue-600
    `,
    secondary: `
      bg-gray-200 hover:bg-gray-300 active:bg-gray-400
      text-gray-800
      focus:ring-gray-500
      dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-100
    `,
    success: `
      bg-green-600 hover:bg-green-700 active:bg-green-800
      text-white
      focus:ring-green-500
      dark:bg-green-500 dark:hover:bg-green-600
    `,
    danger: `
      bg-red-600 hover:bg-red-700 active:bg-red-800
      text-white
      focus:ring-red-500
      dark:bg-red-500 dark:hover:bg-red-600
    `,
    warning: `
      bg-yellow-500 hover:bg-yellow-600 active:bg-yellow-700
      text-white
      focus:ring-yellow-500
      dark:bg-yellow-400 dark:hover:bg-yellow-500 dark:text-gray-900
    `,
    outline: `
      bg-transparent
      border-2 border-blue-600 dark:border-blue-400
      text-blue-600 dark:text-blue-400
      hover:bg-blue-50 dark:hover:bg-blue-900/20
      focus:ring-blue-500
    `,
    ghost: `
      bg-transparent
      text-gray-700 dark:text-gray-300
      hover:bg-gray-100 dark:hover:bg-gray-800
      focus:ring-gray-500
    `,
    link: `
      bg-transparent
      text-blue-600 dark:text-blue-400
      hover:underline
      focus:ring-blue-500
      p-0
    `,
  };

  const sizes = {
    xs: 'px-2 py-1 text-xs',
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-sm',
    lg: 'px-6 py-3 text-base',
    xl: 'px-8 py-4 text-lg',
  };

  const Spinner = () => (
    <svg
      className="animate-spin -ml-1 mr-2 h-4 w-4"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );

  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${className}`}
      {...props}
    >
      {loading && <Spinner />}
      {leftIcon && !loading && <span className="mr-2">{leftIcon}</span>}
      {children}
      {rightIcon && <span className="ml-2">{rightIcon}</span>}
    </button>
  );
});

Button.displayName = 'Button';

export default Button;

